"use client"

import { FC } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import { LazyMotion, domAnimation, m } from "motion/react"
import { vFadeUp, vFadeIn, vStaggerContainer } from "@/lib/animations"

export type TestimonialWithSocialAndFooterProps =
  SliceComponentProps<Content.TestimonialWithSocialAndFooterSlice>

const iconVariant = {
  hidden: { opacity: 0, y: -20, scale: 0.85 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease: [0.34, 1.56, 0.64, 1] as const },
  },
}

const TestimonialWithSocialAndFooter: FC<
  TestimonialWithSocialAndFooterProps
> = ({ slice }) => {
  const { primary } = slice

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative overflow-hidden section-py"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 40%, oklch(0.97 0.04 75) 0%, oklch(0.99 0.01 75) 60%, white 100%)",
        }}
      >
        {/* Ambient blobs */}
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-1/2 size-72 -translate-x-1/2 rounded-full opacity-30 blur-3xl"
          style={{ background: "var(--sacred-gold)" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-1/4 size-40 rounded-full opacity-20 blur-2xl"
          style={{ background: "var(--sacred-orange)" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute right-1/4 bottom-0 size-40 rounded-full opacity-20 blur-2xl"
          style={{ background: "var(--sacred-orange)" }}
        />

        {/* Decorative large quote marks */}
        <span
          aria-hidden
          className="pointer-events-none absolute top-2 left-4 font-serif text-[9rem] leading-none opacity-[0.06] select-none md:left-10 md:text-[14rem]"
          style={{ color: "var(--sacred-red)" }}
        >
          &ldquo;
        </span>
        <span
          aria-hidden
          className="pointer-events-none absolute right-4 bottom-2 rotate-180 font-serif text-[9rem] leading-none opacity-[0.06] select-none md:right-10 md:text-[14rem]"
          style={{ color: "var(--sacred-red)" }}
        >
          &ldquo;
        </span>

        <m.div
          className="relative container flex flex-col items-center gap-8 text-center"
          variants={vStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {/* Icon with halo */}
          {primary.icon_image?.url && (
            <m.div variants={iconVariant} className="relative">
              {/* Golden halo glow */}
              <div
                className="absolute inset-0 scale-[2] rounded-full blur-2xl"
                style={{ background: "var(--sacred-gold)", opacity: 0.45 }}
              />
              <PrismicNextImage
                field={primary.icon_image}
                fallbackAlt=""
                width={80}
                height={80}
                className="relative h-28 w-fit object-cover drop-shadow-xl"
                imgixParams={{ auto: ["format", "compress"], q: 75 }}
              />
            </m.div>
          )}

          {/* Testimonial quote */}
          {primary.testimonial && (
            <m.div variants={vFadeUp} className="max-w-3xl">
              <PrismicRichText
                field={primary.testimonial}
                components={{
                  paragraph: ({ children }) => (
                    <p
                      className="text-xl/relaxed font-bold italic sm:text-2xl md:text-3xl"
                      style={{ color: "var(--sacred-red)" }}
                    >
                      {children}
                    </p>
                  ),
                  heading1: ({ children }) => (
                    <h1
                      className="leading-relaxed italic"
                      style={{ color: "var(--sacred-red)" }}
                    >
                      {children}
                    </h1>
                  ),
                  heading2: ({ children }) => (
                    <h2
                      className="leading-relaxed italic"
                      style={{ color: "var(--sacred-red)" }}
                    >
                      {children}
                    </h2>
                  ),
                }}
              />
            </m.div>
          )}

          {/* Ornamental divider */}
          <m.div variants={vFadeIn} className="flex items-center gap-2">
            <div
              className="h-px w-14 rounded-full"
              style={{ background: "var(--sacred-orange)", opacity: 0.45 }}
            />
            <div
              className="size-1.5 rounded-full"
              style={{ background: "var(--sacred-orange)" }}
            />
            <div
              className="size-2 rounded-full"
              style={{ background: "var(--sacred-orange)" }}
            />
            <div
              className="size-1.5 rounded-full"
              style={{ background: "var(--sacred-orange)" }}
            />
            <div
              className="h-px w-14 rounded-full"
              style={{ background: "var(--sacred-orange)", opacity: 0.45 }}
            />
          </m.div>

          {/* Attribution */}
          <m.div variants={vFadeUp} className="flex flex-col gap-1">
            {primary.footer_main_text && (
              <p
                className="text-lg font-bold tracking-wide"
                style={{ color: "var(--sacred-orange)" }}
              >
                {primary.footer_main_text}
              </p>
            )}
            {primary.footer_sub_text && (
              <p
                className="text-sm tracking-wide"
                style={{ color: "var(--sacred-orange)", opacity: 0.8 }}
              >
                {primary.footer_sub_text}
              </p>
            )}
          </m.div>
        </m.div>
      </section>
    </LazyMotion>
  )
}

export default TestimonialWithSocialAndFooter
